<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditing ? "Editar Rol" : copyFrom ? `Copiar rol «${copyFrom.name}»` : "Crear Rol" }}
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            :disabled="isLoading"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="name" class="form-label">Nombre</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                id="name"
                v-model="formData.name"
                required
                :disabled="
                  (isEditing && formData.name === 'admin') || isLoading
                "
              />
              <div class="invalid-feedback" v-if="errors.name">
                {{ errors.name }}
              </div>
            </div>
            <div class="mb-3">
              <label for="description" class="form-label">Descripción</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model="formData.description"
                required
                :disabled="isLoading"
              />
              <div class="invalid-feedback" v-if="errors.description">
                {{ errors.description }}
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Permisos</label>
              <div
                class="permissions-grid"
                :class="{ 'is-invalid': errors.permissions }"
              >
                <div class="permissions-header">
                  <div class="module-column">Módulo</div>
                  <div class="action-columns">
                    <div class="action-column">
                      Ver
                      <div class="form-check mt-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="select-all-view"
                          :checked="isAllViewSelected"
                          @change="toggleAllView"
                          :disabled="isLoading"
                        />
                        <label class="form-check-label small" for="select-all-view">
                          Todos
                        </label>
                      </div>
                    </div>
                    <div class="action-column">
                      Gestionar
                      <div class="form-check mt-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="select-all-manage"
                          :checked="isAllManageSelected"
                          @change="toggleAllManage"
                          :disabled="isLoading"
                        />
                        <label class="form-check-label small" for="select-all-manage">
                          Todos
                        </label>
                      </div>
                    </div>
                    <div class="action-column">
                      Eliminar
                      <div class="form-check mt-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="select-all-delete"
                          :checked="isAllDeleteSelected"
                          @change="toggleAllDelete"
                          :disabled="isLoading"
                        />
                        <label class="form-check-label small" for="select-all-delete">
                          Todos
                        </label>
                      </div>
                    </div>
                    <div class="action-column">
                      Fila
                      <div class="form-check mt-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="select-all-grid"
                          :checked="isAllGridSelected"
                          @change="toggleAllGrid"
                          :disabled="isLoading"
                        />
                        <label class="form-check-label small" for="select-all-grid">
                          Todos
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="permissions-body">
                  <div
                    v-for="module in getUniqueModules"
                    :key="module"
                    class="permission-row"
                  >
                    <div class="module-column">
                      {{ formatModuleName(module) }}
                    </div>
                    <div class="action-columns">
                      <div class="action-column">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${module}.view`"
                            v-model="formData.permissions"
                            :value="`${module}.view`"
                            :disabled="isLoading"
                          />
                        </div>
                      </div>
                      <div class="action-column">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${module}.manage`"
                            v-model="formData.permissions"
                            :value="`${module}.manage`"
                            :disabled="isLoading"
                          />
                        </div>
                      </div>
                      <div class="action-column">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${module}.delete`"
                            v-model="formData.permissions"
                            :value="`${module}.delete`"
                            :disabled="isLoading"
                          />
                        </div>
                      </div>
                      <div class="action-column">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`${module}.all`"
                            :checked="isRowSelected(module)"
                            :indeterminate.prop="isRowPartial(module)"
                            @change="toggleRow(module, $event)"
                            :disabled="isLoading"
                            title="Ver, gestionar y eliminar"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="invalid-feedback" v-if="errors.permissions">
                {{ errors.permissions }}
              </div>
            </div>

            <!-- Accounting Module Permissions -->
            <div class="mb-3 module-permissions">
              <label class="form-label">Permisos de Contabilidad</label>
              <div >
                <div class="form-check form-check-inline float-left mx-4" v-for="action in accountingActions" :key="action.value">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`accounting.${action.value}`"
                    v-model="formData.permissions"
                    :value="`accounting.${action.value}`"
                    :disabled="isLoading"
                  />
                  <label class="form-check-label" :for="`accounting.${action.value}`">
                    {{ action.label }}
                  </label>
                </div>
              </div>
              <small class="form-text text-muted">
                Permisos específicos para el módulo de contabilidad
              </small>
            </div>

            <!-- Finance Module Permissions -->
            <div class="mb-3 module-permissions">
              <label class="form-label">Permisos de Finance</label>
              <div >
                <div class="form-check form-check-inline float-left mx-4" v-for="action in financeActions" :key="action.value">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`finance.${action.value}`"
                    v-model="formData.permissions"
                    :value="`finance.${action.value}`"
                    :disabled="isLoading"
                  />
                  <label class="form-check-label" :for="`finance.${action.value}`">
                    {{ action.label }}
                  </label>
                </div>
              </div>
              <small class="form-text text-muted">
                Permisos específicos para el módulo de finance. Si no tiene permisos específicos, no podrá acceder al menú /financial pero sí podrá ver el tab Finance en las vistas de detalle.
              </small>
            </div>

            <div class="mb-3 module-permissions">
              <label class="form-label">Permisos adicionales</label>
              <div
                v-for="perm in standalonePermissions"
                :key="perm.value"
                class="standalone-permission-item mb-2"
              >
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`standalone-${perm.value}`"
                    v-model="formData.permissions"
                    :value="perm.value"
                    :disabled="isLoading"
                  />
                  <label class="form-check-label" :for="`standalone-${perm.value}`">
                    {{ perm.label }}
                  </label>
                </div>
                <small v-if="perm.hint" class="form-text text-muted d-block ms-4">
                  {{ perm.hint }}
                </small>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Módulos Permitidos</label>
              <div
                class="modules-grid"
                :class="{ 'is-invalid': errors.allowedModules }"
              >
                <!-- Debug info -->
                <div v-if="activeModules.length === 0" class="text-muted mb-2">
                  <small
                    >🔄 Cargando módulos... ({{
                      activeModules.length
                    }}
                    encontrados)</small
                  >
                </div>

                <div
                  v-for="module in activeModules"
                  :key="module.name"
                  class="module-item"
                >
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="`module-${module.name}`"
                      v-model="formData.allowedModules"
                      :value="module.name"
                      :disabled="isLoading"
                    />
                    <label
                      class="form-check-label"
                      :for="`module-${module.name}`"
                    >
                      {{ module.displayName }}
                    </label>
                  </div>
                </div>
              </div>
              <div class="invalid-feedback" v-if="errors.allowedModules">
                {{ errors.allowedModules }}
              </div>
              <small class="form-text text-muted">
                Selecciona qué módulos puede ver este rol
              </small>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
                :disabled="isLoading"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isLoading"
              >
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ isEditing ? "Guardar" : "Crear" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";

/**
 * Módulos que el rol no restringe, así que su casillero no cambiaba nada: el soporte lo ve
 * cualquier usuario logueado, la operación FOB solo pide el módulo activo en el tenant y la
 * negociación la controlan los permisos del modelo.
 */
const MODULES_NOT_GATED_BY_ROLE = ["support_module", "fobtrading_module", "negotiation_module"];

export default {
  name: "RoleFormModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    role: {
      type: Object,
      default: null,
    },
    /** Rol del que se copian permisos y módulos para crear uno nuevo. */
    copyFrom: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        name: "",
        description: "",
        permissions: [],
        allowedModules: [],
      },
      isLoading: false,
      errors: {},
      availablePermissions: [],
      models: [],
      activeModules: [],
      accountingActions: [
        { value: 'view', label: 'Ver' },
        { value: 'create', label: 'Crear' },
        { value: 'edit', label: 'Editar' },
        { value: 'delete', label: 'Eliminar' },
        { value: 'export', label: 'Exportar' }
      ],
      financeActions: [
        { value: 'manage', label: 'Administrar' },
        { value: 'payments', label: 'Payments' },
        { value: 'collects', label: 'Collects' },
        { value: 'bankAccountStatement', label: 'Bank Account Statement' }
      ],
      standalonePermissions: [
        {
          value: 'email.module.manage',
          label: 'Emails automáticos (activar / desactivar y modo prueba)',
          hint:
            'Permite activar o desactivar el envío automático de emails y configurar el modo prueba en Facturación → Características del sistema, sin acceso al resto de opciones de facturación.',
        },
        {
          value: 'systemfeatures.access',
          label: 'Características del sistema (opciones de facturación)',
          hint:
            'Permite activar o desactivar opciones de facturación: Smart Email, Container Tracking, Chat, Agente IA, etc.',
        },
        {
          value: 'usermenu.apply_to_all',
          label: 'Mi menú: aplicar a todos los usuarios',
          hint:
            'Permite, en Configuración → Mi menú: aplicar el menú entero a todos, añadir o renombrar una sola entrada en el menú de todos, y ordenar el menú de otro usuario. Los administradores no necesitan este permiso.',
        },
        {
          value: 'preferences.apply_to_all',
          label: 'Preferencias de listados: aplicar a todos los usuarios',
          hint:
            'Al guardar preferencias en un listado (columnas y filtros) muestra la opción de aplicarlas a todos los usuarios. Los administradores no necesitan este permiso.',
        },
        {
          value: 'support.technical',
          label: 'Soporte técnico (gestión de tickets)',
          hint:
            'Permite asignar, mover y gestionar tickets de soporte. Los tickets resueltos siguen bloqueados; el rol administrador puede modificarlos.',
        },
      ],
    };
  },
  computed: {
    isEditing() {
      return !!this.role;
    },
    getUniqueModules() {
      const modules = new Set();
      this.availablePermissions.forEach((permission) => {
        const [module] = permission.split(".");
        // Exclude accounting and finance - they have their own sections
        if (
          module === 'accounting' ||
          module === 'finance' ||
          module === 'usermenu' ||
          module === 'systemfeatures' ||
          module === 'support'
        ) {
          return;
        }
        // Permisos sueltos (sin .view/.manage/.delete) no van en la grilla
        const standalone = this.standalonePermissions.some((p) => p.value === permission);
        if (standalone) return;
        // Main models (no underscore): always show
        if (!module.includes('_')) {
          modules.add(module);
          return;
        }
        // Group / derived models (parent_field, ej. shipping_expenses): siempre listar para poder otorgar *.manage
        const groupModel = this.models.find(
          (m) => m.modelName && m.modelName.toLowerCase() === module.toLowerCase() && m.isGroupModel
        );
        if (groupModel) {
          modules.add(module);
        }
      });
      return Array.from(modules).sort();
    },
    // Verificar si todos los permisos "view" están seleccionados
    isAllViewSelected() {
      const viewPermissions = this.getUniqueModules.map(module => `${module}.view`);
      return viewPermissions.length > 0 && viewPermissions.every(perm => 
        this.formData.permissions.includes(perm)
      );
    },
    // Verificar si todos los permisos "manage" están seleccionados
    isAllManageSelected() {
      const managePermissions = this.getUniqueModules.map(module => `${module}.manage`);
      return managePermissions.length > 0 && managePermissions.every(perm => 
        this.formData.permissions.includes(perm)
      );
    },
    isAllGridSelected() {
      return this.isAllViewSelected && this.isAllManageSelected && this.isAllDeleteSelected;
    },
    // Verificar si todos los permisos "delete" están seleccionados
    isAllDeleteSelected() {
      const deletePermissions = this.getUniqueModules.map(module => `${module}.delete`);
      return deletePermissions.length > 0 && deletePermissions.every(perm => 
        this.formData.permissions.includes(perm)
      );
    },
  },
  watch: {
    role: {
      immediate: true,
      handler(newRole) {
        console.log("Role prop changed:", newRole);
        if (newRole) {
          this.formData = {
            name: newRole.name,
            description: newRole.description,
            permissions: [...(newRole.permissions || [])],
            allowedModules: [...(newRole.allowedModules || [])],
          };
        } else if (this.copyFrom) {
          this.formData = {
            name: `${this.copyFrom.name} (copia)`,
            description: this.copyFrom.description || "",
            permissions: [...(this.copyFrom.permissions || [])],
            allowedModules: [...(this.copyFrom.allowedModules || [])],
          };
        } else {
          this.formData = {
            name: "",
            description: "",
            permissions: [],
            allowedModules: [],
          };
        }
      },
    },
    show(newValue) {
      console.log("Show prop changed:", newValue);
      if (!newValue) {
        this.formData = {
          name: "",
          description: "",
          permissions: [],
          allowedModules: [],
        };
        this.errors = {};
      }
    },
    // Watch para sincronizar finance.manage con finance.payments y finance.collects
    'formData.permissions': {
      deep: true,
      handler(newPermissions) {
        // Si se selecciona finance.manage, agregar automáticamente finance.payments
        if (newPermissions.includes('finance.manage') && !newPermissions.includes('finance.payments')) {
          this.formData.permissions.push('finance.payments');
        }
        // Si se selecciona finance.manage, agregar automáticamente finance.collects
        if (newPermissions.includes('finance.manage') && !newPermissions.includes('finance.collects')) {
          this.formData.permissions.push('finance.collects');
        }
        // Si se selecciona finance.manage, agregar automáticamente finance.bankAccountStatement
        if (newPermissions.includes('finance.manage') && !newPermissions.includes('finance.bankAccountStatement')) {
          this.formData.permissions.push('finance.bankAccountStatement');
        }
        // Si se deselecciona finance.manage, mantener los permisos (no se eliminan automáticamente)
      },
    },
  },
  methods: {
    async fetchModels() {
      try {
        const { data } = await wencesApi.get(
          "/api/gotrading/model-definitions"
        );
        this.models = data;
        this.generatePermissions();
      } catch (error) {
        console.error("Error fetching models:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar los modelos",
          icon: "error",
        });
      }
    },
    async fetchActiveModules() {
      try {
        console.log("🔄 Iniciando carga de módulos activos...");

        // Usar el mismo endpoint que usa SystemModules.vue
        const response = await wencesApi.get("/api/modules/all");
        console.log("📡 Respuesta del endpoint /api/modules/all:", response);

        // Filtrar solo los módulos activos, igual que en SystemModules.vue
        if (response.data && Array.isArray(response.data)) {
          this.activeModules = response.data.filter(
            (module) =>
              module.isActive &&
              !MODULES_NOT_GATED_BY_ROLE.includes(module.name)
          );
          console.log("✅ Módulos activos filtrados:", this.activeModules);
        } else {
          console.log("⚠️ Respuesta no es un array:", response.data);
          this.activeModules = [];
        }

        console.log(
          "📋 Total de módulos activos cargados:",
          this.activeModules.length
        );
      } catch (error) {
        console.error("❌ Error fetching active modules:", error);
        this.activeModules = [];
      }
    },
    generatePermissions() {
      const basePermissions = ["view", "manage", "delete"];
      const permissions = [];

      // Agregar permisos para cada modelo
      this.models.forEach((model) => {
        const modelName = model.modelName.toLowerCase();
        basePermissions.forEach((action) => {
          permissions.push(`${modelName}.${action}`);
        });
      });

      // Agregar permisos del sistema
      const systemModules = ["users", "roles", "settings", "lists"];
      systemModules.forEach((module) => {
        basePermissions.forEach((action) => {
          permissions.push(`${module}.${action}`);
        });
      });

      // Agregar permisos de módulos especiales (accounting)
      const accountingPermissions = ["view", "create", "edit", "delete", "export"];
      accountingPermissions.forEach((action) => {
        permissions.push(`accounting.${action}`);
      });

      // Agregar permisos de módulos especiales (finance)
      const financePermissions = ["manage", "payments", "collects", "bankAccountStatement"];
      financePermissions.forEach((action) => {
        permissions.push(`finance.${action}`);
      });

      this.availablePermissions = permissions;
    },
    formatPermission(permission) {
      const [module, action] = permission.split(".");
      return `${module.charAt(0).toUpperCase() + module.slice(1)} - ${
        action.charAt(0).toUpperCase() + action.slice(1)
      }`;
    },
    formatModuleName(module) {
      // Modelos de grupo / derivados: usar label del API si existe (ej. "Shipping - Expenses")
      const model = this.models.find(
        (m) => m.modelName && m.modelName.toLowerCase() === module.toLowerCase()
      );
      if (model && model.label) {
        return model.label;
      }
      return module.charAt(0).toUpperCase() + module.slice(1);
    },
    // Seleccionar/deseleccionar todos los permisos "view"
    toggleAllView(event) {
      const viewPermissions = this.getUniqueModules.map(module => `${module}.view`);
      if (event.target.checked) {
        // Agregar todos los permisos view que no estén ya seleccionados
        viewPermissions.forEach(perm => {
          if (!this.formData.permissions.includes(perm)) {
            this.formData.permissions.push(perm);
          }
        });
      } else {
        // Remover todos los permisos view
        this.formData.permissions = this.formData.permissions.filter(
          perm => !viewPermissions.includes(perm)
        );
      }
    },
    // Seleccionar/deseleccionar todos los permisos "manage"
    toggleAllManage(event) {
      const managePermissions = this.getUniqueModules.map(module => `${module}.manage`);
      if (event.target.checked) {
        // Agregar todos los permisos manage que no estén ya seleccionados
        managePermissions.forEach(perm => {
          if (!this.formData.permissions.includes(perm)) {
            this.formData.permissions.push(perm);
          }
        });
      } else {
        // Remover todos los permisos manage
        this.formData.permissions = this.formData.permissions.filter(
          perm => !managePermissions.includes(perm)
        );
      }
    },
    // Seleccionar/deseleccionar todos los permisos "delete"
    toggleAllDelete(event) {
      const deletePermissions = this.getUniqueModules.map(module => `${module}.delete`);
      if (event.target.checked) {
        // Agregar todos los permisos delete que no estén ya seleccionados
        deletePermissions.forEach(perm => {
          if (!this.formData.permissions.includes(perm)) {
            this.formData.permissions.push(perm);
          }
        });
      } else {
        // Remover todos los permisos delete
        this.formData.permissions = this.formData.permissions.filter(
          perm => !deletePermissions.includes(perm)
        );
      }
    },
    rowPermissions(module) {
      return ["view", "manage", "delete"].map((action) => `${module}.${action}`);
    },
    isRowSelected(module) {
      return this.rowPermissions(module).every((perm) => this.formData.permissions.includes(perm));
    },
    isRowPartial(module) {
      const count = this.rowPermissions(module).filter((perm) =>
        this.formData.permissions.includes(perm)
      ).length;
      return count > 0 && count < 3;
    },
    setPermissions(perms, checked) {
      if (checked) {
        perms.forEach((perm) => {
          if (!this.formData.permissions.includes(perm)) {
            this.formData.permissions.push(perm);
          }
        });
      } else {
        this.formData.permissions = this.formData.permissions.filter(
          (perm) => !perms.includes(perm)
        );
      }
    },
    // Ver, gestionar y eliminar de un modelo con un solo tilde
    toggleRow(module, event) {
      this.setPermissions(this.rowPermissions(module), event.target.checked);
    },
    toggleAllGrid(event) {
      this.setPermissions(
        this.getUniqueModules.flatMap((module) => this.rowPermissions(module)),
        event.target.checked
      );
    },
    closeModal() {
      this.$emit("update:show", false);
    },
    validateForm() {
      this.errors = {};
      if (!this.formData.name.trim()) {
        this.errors.name = "El nombre es requerido";
      }
      if (!this.formData.description.trim()) {
        this.errors.description = "La descripción es requerida";
      }
      if (this.formData.permissions.length === 0) {
        this.errors.permissions = "Debe seleccionar al menos un permiso";
      }
      return Object.keys(this.errors).length === 0;
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        Swal.fire({
          title: "Error de validación",
          text: "Por favor, complete todos los campos requeridos",
          icon: "error",
        });
        return;
      }

      // Debug: mostrar datos que se van a enviar
      console.log("📤 Enviando datos del formulario:", {
        name: this.formData.name,
        description: this.formData.description,
        permissions: this.formData.permissions,
        allowedModules: this.formData.allowedModules,
      });

      this.isLoading = true;
      try {
        let response;
        if (this.isEditing) {
          response = await wencesApi.put(`/api/roles/${this.role._id}`, {
            name: this.formData.name,
            description: this.formData.description,
            permissions: this.formData.permissions,
            allowedModules: this.formData.allowedModules,
          });
        } else {
          response = await wencesApi.post("/api/roles", {
            name: this.formData.name,
            description: this.formData.description,
            permissions: this.formData.permissions,
            allowedModules: this.formData.allowedModules,
          });
        }

        console.log("✅ Respuesta del servidor:", response.data);
        this.$emit("role-updated", response.data.data);
        this.closeModal();

        Swal.fire({
          title: this.isEditing ? "¡Actualizado!" : "¡Creado!",
          text: this.isEditing
            ? "El rol ha sido actualizado correctamente"
            : "El rol ha sido creado correctamente",
          icon: "success",
        });
      } catch (err) {
        console.error("❌ Error saving role:", err);
        Swal.fire({
          title: "Error",
          text:
            err.response?.data?.message || err.response?.data?.error ||
            (this.isEditing
              ? "Error al actualizar el rol"
              : "Error al crear el rol"),
          icon: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchModels();
    await this.fetchActiveModules();
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.modal-content {
  padding: 0;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.modal-body {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.permissions-grid {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.permissions-header {
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 500;
}

.permissions-body {
  max-height: 300px;
  overflow-y: auto;
}

.permission-row {
  display: flex;
  border-bottom: 1px solid #dee2e6;
}

.permission-row:last-child {
  border-bottom: none;
}

.module-column {
  flex: 1;
  padding: 0.75rem;
  min-width: 150px;
}

.action-columns {
  display: flex;
  flex: 1;
}

.action-column {
  flex: 1;
  padding: 0.75rem;
  text-align: center;
  min-width: 100px;
}

.form-check {
  display: flex;
  justify-content: center;
  margin: 0;
}

.permissions-grid.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.form-check-input:disabled {
  cursor: not-allowed;
}

.module-permissions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.module-permissions .form-check-inline {
  margin-right: 0;
}

.modules-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.module-item {
  min-width: 150px;
}
</style>
